import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import {
  Wine, Rocket, Package, Users, Calculator, Check, ExternalLink, Loader2,
  Briefcase, Grape, Settings as Cog, GraduationCap, Handshake,
  Sparkles, Globe2, Megaphone, CalendarDays, TrendingUp,
} from "lucide-react";

const APPLY_PATH = "/apply";

const CATEGORY_I18N = {
  "cat-startup": "join_seg_startups_name",
  "cat-expert": "join_seg_experts_name",
  "cat-producer": "join_seg_producteurs_name",
  "cat-supplier": "join_seg_fournisseurs_name",
  "cat-ecole": "join_seg_ecoles_name",
  "cat-partner": "join_seg_partenaires_name",
};
function catLabel(cat, t) {
  const k = CATEGORY_I18N[cat.id];
  if (!k) return cat.name;
  const v = t(k);
  return v === k ? cat.name : v;
}

const SEGMENTS = [
  {
    id: "startups", icon: Rocket, emoji: "🚀",
    nameKey: "join_seg_startups_name", descKey: "join_seg_startups_desc",
    fees: [
      { range: "< 100 K€", amount: "350 €" },
      { range: "100 K€ – 500 K€", amount: "700 €" },
      { range: "> 500 K€", amount: "1 100 €" },
    ],
    extraKeys: ["join_seg_startups_extra_1","join_seg_startups_extra_2","join_seg_startups_extra_3","join_seg_startups_extra_4","join_seg_startups_extra_5"],
  },
  {
    id: "experts", icon: Briefcase, emoji: "👨‍💼",
    nameKey: "join_seg_experts_name", descKey: "join_seg_experts_desc",
    fees: [
      { range: "< 100 K€", amount: "350 €" },
      { range: "100 K€ – 500 K€", amount: "700 €" },
      { range: "> 500 K€", amount: "1 100 €" },
    ],
    extraKeys: ["join_seg_experts_extra_1","join_seg_experts_extra_2","join_seg_experts_extra_3","join_seg_experts_extra_4"],
  },
  {
    id: "producteurs", icon: Grape, emoji: "🍷",
    nameKey: "join_seg_producteurs_name", descKey: "join_seg_producteurs_desc",
    fees: [
      { range: "< 2 M€", amount: "150 €" },
      { range: "2 M€ – 10 M€", amount: "200 €" },
      { range: "10 M€ – 50 M€", amount: "350 €" },
      { range: "> 50 M€", amount: "500 €" },
    ],
    extraKeys: ["join_seg_producteurs_extra_1","join_seg_producteurs_extra_2","join_seg_producteurs_extra_3","join_seg_producteurs_extra_4","join_seg_producteurs_extra_5"],
  },
  {
    id: "fournisseurs", icon: Cog, emoji: "⚙️",
    nameKey: "join_seg_fournisseurs_name", descKey: "join_seg_fournisseurs_desc",
    fees: [
      { range: "< 1 M€", amount: "500 €" },
      { range: "1 M€ – 10 M€", amount: "800 €" },
      { range: "> 10 M€", amount: "1 500 €" },
    ],
    extraKeys: ["join_seg_fournisseurs_extra_1","join_seg_fournisseurs_extra_2","join_seg_fournisseurs_extra_3","join_seg_fournisseurs_extra_4"],
  },
  {
    id: "ecoles", icon: GraduationCap, emoji: "🎓",
    nameKey: "join_seg_ecoles_name", descKey: "join_seg_ecoles_desc",
    fees: [
      { range: "< 100 K€", amount: "350 €" },
      { range: "100 K€ – 500 K€", amount: "700 €" },
      { range: "> 500 K€", amount: "1 100 €" },
    ],
    extraKeys: ["join_seg_ecoles_extra_1","join_seg_ecoles_extra_2","join_seg_ecoles_extra_3","join_seg_ecoles_extra_4"],
  },
  {
    id: "partenaires", icon: Handshake, emoji: "🤝",
    nameKey: "join_seg_partenaires_name", descKey: "join_seg_partenaires_desc",
    fees: [
      { range: "Partenaire WineTech", amount: "5 000 €" },
    ],
    extraKeys: ["join_seg_partenaires_extra_1","join_seg_partenaires_extra_2","join_seg_partenaires_extra_3","join_seg_partenaires_extra_4","join_seg_partenaires_extra_5"],
  },
];

const COMMON_BENEFIT_KEYS = [
  "join_common_1","join_common_2","join_common_3","join_common_4","join_common_5",
  "join_common_6","join_common_7","join_common_8","join_common_9",
];

const PROGRAMS = [
  { icon: Sparkles, titleKey: "join_prog_accel_title", emoji: "🚀", descKey: "join_prog_accel_desc" },
  { icon: Globe2, titleKey: "join_prog_export_title", emoji: "🌍", descKey: "join_prog_export_desc" },
  { icon: GraduationCap, titleKey: "join_prog_academy_title", emoji: "🎓", descKey: "join_prog_academy_desc" },
  { icon: Megaphone, titleKey: "join_prog_press_title", emoji: "📣", descKey: "join_prog_press_desc" },
  { icon: CalendarDays, titleKey: "join_prog_events_title", emoji: "🎪", descKey: "join_prog_events_desc" },
];

export default function JoinPage() {
  const { t } = useLang();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  // Simulator state
  const [simCat, setSimCat] = useState("");
  const [revenue, setRevenue] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => {
    api.get("/public/membership-pricing").then((r) => {
      setCats(r.data);
      if (r.data.length > 0) setSimCat(r.data[0].id);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const simulate = async (e) => {
    e.preventDefault();
    setSimLoading(true);
    setSimResult(null);
    try {
      const { data } = await api.post("/public/simulate-fee", { category_id: simCat, revenue: parseFloat(revenue) || 0 });
      setSimResult(data);
    } catch (err) {
      setSimResult({ error: err.response?.data?.detail || "Erreur de simulation" });
    } finally { setSimLoading(false); }
  };

  const fmtMoney = (amt, cur) => {
    const sym = { eur: "€", usd: "$", gbp: "£" }[cur] || "€";
    return `${(amt || 0).toLocaleString("fr-FR")} ${sym}`;
  };

  return (
    <PublicLayout>
      <Seo title={t("comp_joinpage_90")} description="Rejoignez l'écosystème international de l'innovation vitivinicole. Startups, producteurs, fournisseurs, experts, écoles et partenaires — découvrez les tarifs d'adhésion et simulez votre cotisation en quelques secondes." path="/join" />

      {/* HERO */}
      <section className="mx-auto max-w-6xl px-6 pt-12 pb-8">
        <div className="text-center">
          <div className="mx-auto h-1 w-12 rounded-full bg-wine-500" />
          <h1 className="mt-4 font-serif text-4xl text-navy md:text-5xl lg:text-6xl">{t("comp_joinpage_91")} <span className="font-display italic text-wine-600" style={{ fontWeight: 500 }}>{`l'innovation vitivinicole`}</span></h1>
          <p className="mx-auto mt-5 max-w-3xl text-base text-neutral-700 md:text-lg">
            La WineTech fédère startups, producteurs, fournisseurs, experts, établissements d&apos;enseignement et partenaires institutionnels afin d&apos;accélérer l&apos;innovation dans la filière vin.
          </p>
          <div className="mx-auto mt-6 max-w-3xl rounded-3xl border border-wine-200 bg-gradient-to-br from-wine-50 via-rose-50/30 to-white p-6 text-left md:p-8">
            <p className="text-sm font-bold text-wine-700 md:text-base">
              La WineTech n&apos;est pas un simple annuaire. C&apos;est un <span className="text-navy">{t("comp_joinpage_92")}</span> qui connecte les acteurs de la filière, favorise les collaborations et facilite l&apos;émergence de projets à impact pour l&apos;ensemble du secteur.
            </p>
            <p className="mt-3 text-sm text-neutral-700">
              L&apos;adhésion vous permet d&apos;intégrer cette communauté, de participer à ses événements, de bénéficier de sa visibilité collective et d&apos;accéder aux opportunités de collaboration développées au sein de l&apos;écosystème.
            </p>
          </div>
        </div>
      </section>

      {/* COMMON BENEFITS */}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="rounded-3xl border border-neutral-100 bg-white p-8 md:p-10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-700">
                <Check className="h-3.5 w-3.5" /> Inclus pour tous les membres
              </div>
              <h2 className="mt-3 text-3xl font-bold text-navy md:text-4xl">{t("comp_joinpage_93")}</h2>
              <p className="mt-2 max-w-xl text-sm text-neutral-600">{t("comp_joinpage_94")}</p>
            </div>
          </div>
          <ul className="mt-6 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="common-benefits">
            {COMMON_BENEFIT_KEYS.map((k) => (
              <li key={k} className="flex items-start gap-2 text-sm text-neutral-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {t(k)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 text-center">
          <div className="mx-auto h-1 w-12 rounded-full bg-wine-500" />
          <h2 className="mt-4 text-3xl font-bold text-navy md:text-4xl">{t("comp_joinpage_95")}</h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-neutral-600">{t("comp_joinpage_96")}</p>
        </div>
        <div className="space-y-6" data-testid="segments-list">
          {SEGMENTS.map((seg) => {
            const I = seg.icon;
            return (
              <div key={seg.id} className="overflow-hidden rounded-3xl border border-wine-100 bg-white shadow-sm transition-all hover:shadow-md" data-testid={`segment-${seg.id}`}>
                <div className="grid gap-0 md:grid-cols-[1fr_auto]">
                  {/* Left: info + extras */}
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4">
                      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-wine-50 text-wine-600">
                        <I className="h-6 w-6" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-xl font-bold text-navy md:text-2xl">
                          <span className="mr-2">{seg.emoji}</span>{t(seg.nameKey)}
                        </h3>
                        <p className="mt-1 text-sm text-neutral-600">{t(seg.descKey)}</p>
                      </div>
                    </div>
                    <div className="mt-5">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-wine-600">{t("comp_joinpage_97")}</div>
                      <ul className="mt-2 space-y-1.5">
                        {seg.extraKeys.map((k) => (
                          <li key={k} className="flex items-start gap-2 text-sm text-neutral-700">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {t(k)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {/* Right: fee grid */}
                  <div className="bg-gradient-to-br from-wine-50/60 to-white p-6 md:min-w-[260px] md:border-l md:border-wine-100 md:p-8">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-wine-600">{t("join_annual_fee")}</div>
                    <div className="mt-3 space-y-2">
                      {seg.fees.map((f, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 text-sm shadow-sm">
                          <span className="text-neutral-700">{f.range}</span>
                          <span className="font-bold text-wine-700">{f.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PRICING SIMULATOR — kept intact, just re-titled */}
      {cats.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-10" id="simulateur">
          <div className="rounded-3xl border border-wine-200 bg-gradient-to-br from-wine-50 to-white p-8 md:p-10" data-testid="pricing-simulator">
            <div className="grid gap-8 md:grid-cols-2 md:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wider text-wine-700 shadow-sm">
                  <Calculator className="h-3.5 w-3.5" /> {t("join_calc_chip")}
                </div>
                <h2 className="mt-5 text-3xl font-bold text-navy md:text-4xl">{t("comp_joinpage_98")} <span className="font-display italic text-wine-600" style={{ fontWeight: 500 }}>{t("join_calc_word_fee")}</span></h2>
                <p className="mt-3 text-sm text-neutral-600 md:text-base">{t("comp_joinpage_99")}</p>
              </div>
              <form onSubmit={simulate} className="space-y-3 rounded-2xl border border-wine-100 bg-white p-6 shadow-sm">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">{t("comp_joinpage_100")}</label>
                  <select required value={simCat} onChange={(e) => setSimCat(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm" data-testid="sim-category">
                    {cats.map((c) => <option key={c.id} value={c.id}>{catLabel(c, t)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">{t("join_calc_revenue_label")}</label>
                  <input required type="number" min="0" value={revenue} onChange={(e) => setRevenue(e.target.value)} placeholder={t("join_calc_revenue_placeholder")} className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm" data-testid="sim-revenue" />
                </div>
                <button type="submit" disabled={simLoading} className="w-full rounded-full bg-wine-500 px-5 py-3 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="sim-submit">
                  {simLoading ? t("join_calc_loading") : t("join_calc_submit")}
                </button>
                {simResult && !simResult.error && (
                  <div className="mt-3 rounded-xl bg-emerald-50 p-4 text-center" data-testid="sim-result">
                    <div className="text-xs font-semibold text-emerald-700">{t("join_calc_bracket")} : {simResult.bracket_label}</div>
                    <div className="mt-1 text-3xl font-bold text-emerald-700">{fmtMoney(simResult.fee, simResult.currency)}</div>
                    <div className="mt-1 text-[10px] text-emerald-600">{t("comp_joinpage_101")}</div>
                  </div>
                )}
                {simResult?.error && <div className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">{simResult.error}</div>}
              </form>
            </div>
          </div>
        </section>
      )}

      {/* BEYOND MEMBERSHIP */}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="text-center">
          <div className="mx-auto h-1 w-12 rounded-full bg-wine-500" />
          <h2 className="mt-4 text-3xl font-bold text-navy md:text-4xl">{t("comp_joinpage_102")}</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-neutral-700 md:text-base">{t("join_beyond_membership_desc")}</p>
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="programs-list">
          {PROGRAMS.map((p, i) => {
            const I = p.icon;
            return (
              <div key={i} className="rounded-2xl border border-wine-100 bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-wine-50 text-wine-600">
                    <I className="h-5 w-5" />
                  </div>
                  <h3 className="text-base font-bold text-navy"><span className="mr-1">{p.emoji}</span>{t(p.titleKey)}</h3>
                </div>
                <p className="mt-3 text-sm text-neutral-600">{t(p.descKey)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHY JOIN + CTA */}
      <section className="mx-auto max-w-6xl px-6 pb-16 pt-6">
        <div className="rounded-3xl border border-wine-100 bg-gradient-to-br from-wine-50 to-white p-10 md:p-14 text-center">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wider text-wine-700 shadow-sm">
            <TrendingUp className="h-3.5 w-3.5" /> Pourquoi adhérer ?
          </div>
          <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-bold text-navy md:text-4xl">
            Pour gagner du temps dans votre veille, développer votre réseau, identifier de nouvelles opportunités de collaboration et participer activement à la <span className="font-display italic text-wine-600" style={{ fontWeight: 500 }}>transformation</span> de la filière vitivinicole.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-neutral-700">
            La WineTech rassemble aujourd&apos;hui les acteurs qui construisent le vin de demain. 🍷🚀
          </p>
          <Link to={APPLY_PATH} className="mt-8 inline-flex items-center gap-2 rounded-full bg-wine-500 px-8 py-4 text-sm font-bold text-white shadow-md transition-all hover:bg-wine-600 hover:shadow-lg" data-testid="apply-open-fillout">
            Démarrer ma candidature <ExternalLink className="h-4 w-4" />
          </Link>
          <p className="mt-5 text-xs text-neutral-500">{t("comp_joinpage_103")} <a href="/login" className="font-semibold text-wine-700 hover:underline">{t("comp_joinpage_104")}</a></p>
        </div>
      </section>

      {/* Hidden DB pricing cards (used to keep the simulator categories synced — kept for fallback display) */}
      {loading && <div className="text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-wine-500" /></div>}
      <div className="hidden" data-testid="pricing-cards">{cats.map((c) => <div key={c.id} data-testid={`pricing-card-${c.id}`}>{c.name}</div>)}</div>
    </PublicLayout>
  );
}
